import { useEffect, useState } from 'react';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { precoNormalizado } from '@profitlens/calculo';
import { db } from '../config/firebase';
import type { AnuncioMercado } from '../tipos';

/**
 * Pesquisa de mercado: quanto o produto está sendo vendido por aí.
 *
 * A coleta automática está bloqueada — toda a API do Mercado Livre passou a
 * exigir token, e a busca de anúncios responde 403 (pendência P1). Então a
 * entrada é MANUAL: a pessoa abre o marketplace, olha, e registra o que viu.
 *
 * O planejamento já previa esse cenário, e é por isso que o resto do sistema não
 * muda: o preço de referência é calculado do mesmo jeito, venha o anúncio de
 * onde vier. Quando a busca abrir, entra outra origem e nada mais se mexe.
 *
 * Cada anúncio guarda `origem`. Um dia vão conviver aqui anúncio digitado e
 * anúncio coletado, e a diferença de confiança entre os dois precisa continuar
 * visível — do mesmo jeito que a resposta de cotação distingue MANUAL de IA.
 */

export type RascunhoAnuncio = Omit<AnuncioMercado, 'id' | 'coletadoEm' | 'precoUnitarioNormCents'>;

export function anuncioVazio(): RascunhoAnuncio {
  return {
    anuncioIdExterno: null,
    titulo: '',
    vendedorNome: null,
    precoCents: null,
    qtdPorPacoteMilli: 1_000,
    condicao: 'novo',
    freteGratis: null,
    reputacaoVendedor: null,
    quantidadeVendida: null,
    link: null,
  };
}

const caminho = (produtoId: string) => `consultasMercado/${produtoId}/anuncios`;

export function useAnuncios(produtoId: string | null) {
  const [anuncios, setAnuncios] = useState<AnuncioMercado[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    if (!produtoId) {
      setAnuncios([]);
      setCarregando(false);
      return;
    }
    setCarregando(true);
    return onSnapshot(
      query(collection(db, caminho(produtoId)), orderBy('precoUnitarioNormCents')),
      (s) => {
        setAnuncios(s.docs.map((d) => ({ id: d.id, ...d.data() }) as AnuncioMercado));
        setCarregando(false);
      },
      () => setCarregando(false),
    );
  }, [produtoId]);

  return { anuncios, carregando };
}

/**
 * Grava o anúncio já com o preço NORMALIZADO por unidade.
 *
 * Sem normalizar, um kit com 3 peças a R$ 90 entraria na média como se fosse um
 * item de R$ 90 — e puxaria a referência para cima como se o mercado pagasse
 * três vezes mais. É o erro que faz o produto parecer viável por causa de uma
 * embalagem diferente.
 */
export async function salvarAnuncio(
  produtoId: string,
  id: string | null,
  dados: RascunhoAnuncio,
  usuarioId: string,
): Promise<void> {
  const ref = id
    ? doc(db, caminho(produtoId), id)
    : doc(collection(db, caminho(produtoId)));

  // Sem pacote informado, uma unidade. Não é chute: é o caso normal, e a tela
  // já entrega 1 por padrão — a guarda existe para dado antigo ou colado.
  const porPacote = dados.qtdPorPacoteMilli && dados.qtdPorPacoteMilli > 0 ? dados.qtdPorPacoteMilli : 1_000;
  const porUnidade =
    dados.precoCents !== null ? precoNormalizado(dados.precoCents, porPacote) : null;

  await setDoc(
    ref,
    {
      ...dados,
      precoUnitarioNormCents: porUnidade,
      origem: 'MANUAL',
      coletadoEm: serverTimestamp(),
      registradoPor: usuarioId,
    },
    { merge: true },
  );

  // O documento-pai marca quando esta pesquisa foi mexida pela última vez. É o
  // que permite a análise dizer "estes preços têm 40 dias" em vez de tratá-los
  // como se fossem de hoje.
  await setDoc(
    doc(db, 'consultasMercado', produtoId),
    { produtoId, atualizadoEm: serverTimestamp(), atualizadoPor: usuarioId },
    { merge: true },
  );
}

export const removerAnuncio = (produtoId: string, id: string) =>
  deleteDoc(doc(db, caminho(produtoId), id));
